// The `/user/email-change/**` flow — four api fns carved out of the four app
// mutation hooks that declared them inline (ARCFE-1105, out-of-hub re-home):
// `src/hooks/mutation/use{ChangeEmailRequest,CheckCurrentEmail,ConfirmEmailChange,VerifyCurrentEmailRequest}.ts`.
//
// WHY THEY WERE OUT OF REACH OF EVERY GATE, and why that is the whole point of
// moving them. Each route is `/user/**`, which is this domain's by measurement
// (`workspace:36 · commerce:3 · coding:1`), but none of the four files is in
// `API_HUB`, so the ARCFE-1064 endpoint admission gate never saw them; and
// neither of this family's `apiHubs` arms reaches them either, because the
// first requires `[Uu]ser` in the SYMBOL and these hooks are named for the
// VERB (`useChangeEmailRequest`) rather than the resource. That is the
// ARCFE-1060 filter-#3 shape read from the route side. The only thing that
// could see them was the ARCFE-1078/1099 presence guard, which froze them.
//
// ONLY THE TRANSPORT MOVED. The hooks stay app-side and keep their bodied
// delegate shape, because each one is genuinely T2 by the section-7a ladder:
// the toast copy interpolates `error.response?.data?.msg` (and, in
// `useConfirmEmailChange`, `data.new_email`), the copy is read through the
// legacy i18next `useTranslations` hook and every hook fires
// `track({ event_name: ButtonClicked, … })`. A `meta` toast cannot carry a
// computed value, so the delegate is the honest tier.
//
// Bodies are byte-identical to the pre-move source: same routes, same verb,
// same request bodies, same ARITY (`requestCurrentEmailVerification` posts
// with ONE argument, no body — pinned in `email-change.test.ts`, because an
// "obviously harmless" `, undefined` changes what axios sends). Transport comes
// from the `@heidi/api` configured-client registry (E5), which hands back the
// app's real `scribeHttpV2` singleton — same interceptors, same auth/refresh
// state.

import { getScribeHttpClients } from '@heidi/api/client';
import { type components } from '@heidi/schema/ml-scribe';

import { type WorkspaceHttpClient } from '../config';

// Structural aliases of the schema names the pre-move hooks used, declared
// here for the same reason `packages/session/src/data/api/transcription.ts`
// declares its own: an api module names its wire shapes, and the hooks keep
// exporting their own aliases so no importer changes.
type RequestEmailChangeRequest = components['schemas']['RequestEmailChangeRequest'];
type RequestEmailChangeResponse = components['schemas']['RequestEmailChangeResponse'];
type CheckCurrentEmailRequest = components['schemas']['CheckCurrentEmailRequest'];
type CheckCurrentEmailResponse = components['schemas']['CheckCurrentEmailResponse'];
type ConfirmEmailChangeRequest = components['schemas']['ConfirmEmailChangeRequest'];
type ConfirmEmailChangeResponse = components['schemas']['ConfirmEmailChangeResponse'];
type VerifyCurrentEmailResponse = components['schemas']['VerifyCurrentEmailResponse'];

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

/**
 * Start an email change: ask the backend to send a verification code to the
 * NEW address.
 *
 * @domain workspace
 */
export const requestEmailChange = (data: RequestEmailChangeRequest) => {
  return http().post<RequestEmailChangeResponse>('/user/email-change/request', data);
};

/**
 * Ask the backend to send a verification code to the user's CURRENT address.
 *
 * Posts with ONE argument and no body, exactly as the pre-move hook did.
 *
 * @domain workspace
 */
export const requestCurrentEmailVerification = () => {
  return http().post<VerifyCurrentEmailResponse>(
    '/user/email-change/verify-current-email/request'
  );
};

/**
 * Check the code the user entered for their CURRENT address.
 *
 * @domain workspace
 */
export const checkCurrentEmail = (data: CheckCurrentEmailRequest) => {
  return http().post<CheckCurrentEmailResponse>(
    '/user/email-change/verify-current-email/check',
    data
  );
};

/**
 * Confirm the change with the code sent to the NEW address. The response
 * carries `new_email`, which the app-side delegate interpolates into its
 * success toast.
 *
 * @domain workspace
 */
export const confirmEmailChange = (data: ConfirmEmailChangeRequest) => {
  return http().post<ConfirmEmailChangeResponse>('/user/email-change/confirm', data);
};
